// Square Code V2 Agent Command — Surface Directory (commit_3).
//
// The five canonical, already-shipped agent surfaces this page links out
// to. Extracted from the original inline "Canonical surfaces" grid
// (Agent Runtime Reconciliation, restyle 2026-07-14) into its own named
// component so it can be tested and evolved independently — same ids,
// labels, descriptions, PATHS, and status tags; only the presentation
// moved. The `.sqv2-agents-surfgrid` / `.sqv2-agents-surf` hover CSS
// still lives in the parent AgentCommandCenter's shared <style> block.
//
// This reuses, never re-forks, the five destinations: Agent Control
// Center, Agent Workspace, Activation Doctor, Engine Runtime V4, and
// Operations Agent Command.

import { commandCenterPath } from '../commandCenterRoutes'
import type { CSSProperties } from 'react'
import { AGENT_FONT, AGENT_STATUS_ACCENT, AGENT_VOID } from './theme'

export interface CanonicalSurface {
  id: string
  label: string
  description: string
  /** Absent for retired surfaces awaiting their hub re-home — renders non-clickable. */
  path?: string
  tag: { label: string; accent: string }
}

// Ids/labels/descriptions/paths are unchanged from the pre-restyle
// version (behavior preserved); `tag` reflects real, verified system
// state (see docs/RUNBOOK.md + this session's Engine V4 ground truth)
// rather than a fabricated "all green" claim.
export const CANONICAL_SURFACES: CanonicalSurface[] = [
  {
    id: 'agent-control-center',
    label: 'Agent Control Center',
    description: 'Roster, skills, lanes, permission boundaries, pause/activate control (owner/admin only). Retired from the legacy shell — re-homes into this hub in a follow-up lane; the backing APIs stay live.',
    tag: { label: 'RE-HOMING', accent: AGENT_STATUS_ACCENT.amber },
  },
  {
    id: 'agent-workspace',
    label: 'Agent Workspace',
    description: 'Threads, tasks, and the PR-style approval queue — where agent-prepared actions are decided. Retired from the legacy shell — re-homes into this hub in a follow-up lane; the backing APIs stay live.',
    tag: { label: 'RE-HOMING', accent: AGENT_STATUS_ACCENT.amber },
  },
  {
    id: 'activation-doctor',
    label: 'Activation Doctor',
    description: 'Self-reported deployment, env, database, Google redirect, and agent-registry readiness. Retired from the legacy shell — re-homes into this hub in a follow-up lane; the backing APIs stay live.',
    tag: { label: 'RE-HOMING', accent: AGENT_STATUS_ACCENT.amber },
  },
  {
    id: 'engine-v4',
    label: 'Engine Runtime V4',
    description: 'Operational runtime — worker leases, autorun, idempotency, approval-bound outbox, task memory.',
    // Honest, exact: all 8 sq_engine_* tables exist live; its 6 execution
    // functions are not deployed. Deferred by operator choice — never
    // shown as fully "ready" or as "not available at all".
    tag: { label: 'TABLES LIVE · FUNCTIONS DEFERRED', accent: AGENT_STATUS_ACCENT.amber },
  },
  {
    id: 'operations-agent-command',
    label: 'Operations Agent Command',
    description: 'The Operations-native entry point to the same command dispatcher, with a live approval-queue view.',
    path: commandCenterPath('operations/agent-command'),
    tag: { label: 'OPEN', accent: AGENT_STATUS_ACCENT.green },
  },
]

export interface SurfaceDirectoryProps {
  onNavigate: (path: string) => void
}

export default function SurfaceDirectory({ onNavigate }: SurfaceDirectoryProps) {
  const indexBadgeBase: CSSProperties = {
    width: 22,
    height: 22,
    borderRadius: 6,
    display: 'grid',
    placeItems: 'center',
    fontFamily: AGENT_FONT.mono,
    fontSize: 8.5,
    fontWeight: 800,
    flexShrink: 0,
  }

  return (
    <div className="sqv2-agents-surfgrid">
      {CANONICAL_SURFACES.map((surface, i) => (
        <button
          key={surface.id}
          type="button"
          className="sqv2-agents-surf"
          onClick={surface.path ? () => onNavigate(surface.path as string) : undefined}
          disabled={!surface.path}
          title={surface.path ? undefined : 'Retired from the legacy shell — awaiting its hub re-home. Backing APIs stay live.'}
          style={{
            textAlign: 'left',
            borderRadius: 12,
            border: `1px solid ${AGENT_VOID.hairline}`,
            background: 'rgba(14,10,22,0.4)',
            padding: '13px 14px',
            color: AGENT_VOID.ink,
            cursor: surface.path ? 'pointer' : 'default',
            opacity: surface.path ? 1 : 0.75,
            font: 'inherit',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 9, marginBottom: 6, flexWrap: 'wrap', rowGap: 6 }}>
            <span
              style={{
                ...indexBadgeBase,
                color: AGENT_VOID.faint,
                border: `1px solid ${AGENT_VOID.hairline}`,
                background: 'rgba(150,140,220,0.06)',
              }}
            >
              {String(i + 1).padStart(2, '0')}
            </span>
            <span style={{ fontFamily: AGENT_FONT.display, fontWeight: 600, fontSize: 14, color: AGENT_VOID.ink }}>
              {surface.label}
            </span>
            <span style={{ flex: 1 }} />
            <span
              style={{
                fontFamily: AGENT_FONT.mono,
                fontSize: 7.5,
                fontWeight: 800,
                letterSpacing: '0.04em',
                padding: '2px 7px',
                borderRadius: 5,
                border: `1px solid ${surface.tag.accent}66`,
                color: surface.tag.accent,
                background: `${surface.tag.accent}10`,
                whiteSpace: 'nowrap',
              }}
            >
              {surface.tag.label}
            </span>
          </div>
          <div style={{ fontSize: 11, color: AGENT_VOID.muted, lineHeight: 1.5 }}>{surface.description}</div>
        </button>
      ))}
    </div>
  )
}
